import { computed, readonly, ref } from 'vue'

import { isStandalone } from '@/shared/utils/browser'

/**
 * Установка кабинета как приложения (PWA).
 *
 * Chrome/Android/десктоп присылают `beforeinstallprompt` ОДИН раз и рано —
 * часто до того, как человек дошёл до профиля. Поэтому слушатели вешаются на
 * уровне модуля при первом импорте (main.ts импортирует его на старте), а
 * отложенный prompt хранится здесь до клика по кнопке в профиле.
 *
 * Safari/iOS такого события не знает: там остаётся только инструкция
 * «Поделиться → На экран „Домой“» (PwaInstallModal).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)

function onBeforeInstallPrompt(event: Event): void {
  // Без preventDefault Chrome показал бы свою мини-плашку сам, вне профиля.
  event.preventDefault()
  deferredPrompt.value = event as BeforeInstallPromptEvent
}

function onAppInstalled(): void {
  installed.value = true
  deferredPrompt.value = null
}

if (typeof window !== 'undefined') {
  installed.value = isStandalone()
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  window.addEventListener('appinstalled', onAppInstalled)
}

export function usePwaInstall() {
  const canPrompt = computed(() => deferredPrompt.value !== null)

  /**
   * Показать системный диалог установки. `true` — человек согласился.
   * Без отложенного события (Safari, уже установлено) — `false`: тогда
   * вызывающий показывает инструкцию.
   */
  async function prompt(): Promise<boolean> {
    const event = deferredPrompt.value
    if (!event) return false

    await event.prompt()
    const { outcome } = await event.userChoice
    // Событие одноразовое: после ответа Chrome его больше не пришлёт.
    deferredPrompt.value = null
    if (outcome === 'accepted') installed.value = true

    return outcome === 'accepted'
  }

  return { canPrompt, isInstalled: readonly(installed), prompt }
}

/** Только для тестов: вернуть модуль в исходное состояние. */
export function resetPwaInstallForTests(): void {
  deferredPrompt.value = null
  installed.value = isStandalone()
}
